import React, { useEffect, useState, useMemo } from 'react'
import { FormControl, TextField, Select, MenuItem, Autocomplete, Alert, Snackbar, Tabs, Tab, Box, CircularProgress, Typography, Badge, styled } from '@mui/material'
import _, { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';
import { encryption, encryptString, decryptString, stringifyJSONAttributes } from '../../../../utils/utilFunctions';
import { api } from '../../../../redux/api';
import { useDispatch, useSelector } from 'react-redux';
import CustomHeaders from '../HttpCustomHeaders/CustomHeaders';
import { contentTypeOptions, requestTypeOptions } from '../../../../constants/constantList';
import { treeActions } from '../../../../redux/slices/treeSlice';
import WorkflowSessionModal from '../../../ItemProperties/WorkflowSessionModal';
import TabPanel from '../../../TabPanel';
import NoAuthFields from './Authorization/NoAuthFields';
import BasicAuthFields from './Authorization/BasicAuthFields';
import BearerTokenFields from './Authorization/BearerTokenFields';
import JwtBearerFields, { JWT_ADD_TO_OPTIONS } from './Authorization/JwtBearerFields';
import OAuth2Fields from './Authorization/OAuth2Fields';
import { AddToOptions } from './Authorization/OAuth2Fields';
import WorkflowSessionModalButton from './Authorization/WorkflowSessionModalButton';
import Settings from './Settings';
const Headers = React.lazy(() => import('./Headers'));
const QueryParameters = React.lazy(() => import('./QueryParameters'));

const Http = ({ connectionDetails, handleBodyContent, connectionName, direction, account, screen, useConnection, repoPDC, disableControl, source = "" }) => {
    const shouldEncryptDecryptPassword = String(source || "").toLowerCase() !== "pdc";
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { rightTree } = useSelector((state) => state.treeData[state.treeData.currentScreen]);
    const currentTab = rightTree.currentTab;
    const selectedNode = rightTree.tree[currentTab].selectedNode;
    const connection = useSelector((state) => state?.treeData?.inbound?.connection);
    const [url, setUrl] = useState('');
    const [privateKeys, setPrivateKeys] = useState([]);
    const [privateKey, setPrivateKey] = useState('');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [openModal, setOpenModal] = useState(false);
    const [customHeaders, setCustomHeaders] = useState('');
    const [requestType, setRequestType] = useState('');
    const [contentTypeOption, setContentTypeOption] = useState(contentTypeOptions);
    const [contentType, setContentType] = useState('');
    const [soapAction, setSoapAction] = useState('');
    const [keyAliases, setKeyAliases] = useState([]);
    const [keyAlias, setKeyAlias] = useState('');
    const [content, setContent] = useState(null);
    const [showAlert, setShowAlert] = useState({ mssg: '', type: 'success', isOpen: false });
    const [valueLabel, setValueLabel] = useState("");
    const [workflowSessionModal, setWorkflowSessionModal] = useState({
        open: false,
        isFirstTime: false,
    });
    const [activeTab, setActiveTab] = useState(2);
    const [authType, setAuthType] = useState('NO_AUTH');
    const [tokenName, setTokenName] = useState('');
    // OAuth 2.0 state
    const [oauth2Token, setOauth2Token] = useState('');
    const [oauth2HeaderPrefix, setOauth2HeaderPrefix] = useState('');
    const [oauth2AutoRefresh, setOauth2AutoRefresh] = useState(false);
    const [oauth2AddTo, setOauth2AddTo] = useState('REQUEST_HEADER');
    const [oauth2GrantType, setOauth2GrantType] = useState('PASSWORD_CREDENTIALS');
    const [oauth2AccessTokenUrl, setOauth2AccessTokenUrl] = useState('');
    const [oauth2ClientId, setOauth2ClientId] = useState('');
    const [oauth2ClientSecret, setOauth2ClientSecret] = useState('');
    const [oauth2Username, setOauth2Username] = useState('');
    const [oauth2Password, setOauth2Password] = useState('');
    const [oauth2ClientAuth, setOauth2ClientAuth] = useState('');
    const [oauth2Scope, setOauth2Scope] = useState('');
    const [oauth2State, setOauth2State] = useState('');
    const [oauth2RefreshTokenUrl, setOauth2RefreshTokenUrl] = useState('');
    // JWT Bearer state
    const [jwtAddTo, setJwtAddTo] = useState('REQUEST_HEADER');
    const [oauth2AuthorizationUrl, setOauth2AuthorizationUrl] = useState('');
    const [oauth2RedirectUri, setOauth2RedirectUri] = useState('');
    const [oauth2AuthorizationCode, setOauth2AuthorizationCode] = useState('');
    const [oauth2CodeVerifier, setOauth2CodeVerifier] = useState('');
    const [oauth2AuthorizationAdditionalParams, setOauth2AuthorizationAdditionalParams] = useState('');
    const [queryParams, setQueryParams] = useState('');

    // Compute the Authorization header value to display in the Headers tab.
    // Values that start and end with '%' are treated as runtime-generated tokens.
    const authHeaderValue = useMemo(() => {
        switch (authType) {
            case 'NO_AUTH':
                return '';
            case 'TRUE': {
                const isUsernameVar = username?.startsWith('%') && username?.endsWith('%');
                const isPasswordVar = password?.startsWith('%') && password?.endsWith('%');
                if (isUsernameVar || isPasswordVar) return '%BASIC_AUTH_CREDENTIALS%';
                if (username || password) {
                    try {
                        return `Basic ${btoa(`${username || ''}:${password || ''}`)}`;
                    } catch {
                        return '';
                    }
                }
                return '';
            }
            case 'BEARER_TOKEN':
                return `Bearer_token${tokenName || ''}`;
            case 'JWT_BEARER':
                return '%JWT_BEARER_TOKEN%';
            case 'OAUTH2':
                return '%OAUTH2_ACCESS_TOKEN%';
            default:
                return '';
        }
    }, [authType, username, password, tokenName]);

    const RECORD_SEP = '\u001e';
    const UNIT_SEP = '\u001f';

    const countPairs = (str) => {
        if (!str) return 0;
        return str.split(RECORD_SEP).filter(Boolean).filter((pair) => {
            const key = pair.slice(0, pair.indexOf(UNIT_SEP) >= 0 ? pair.indexOf(UNIT_SEP) : pair.length);
            return Boolean(key);
        }).length;
    };

    const authCount = useMemo(() => (authType !== 'NO_AUTH' ? 1 : 0), [authType]);
    const headersCount = useMemo(() => countPairs(customHeaders), [customHeaders]);
    const queryParamsCount = useMemo(() => countPairs(queryParams), [queryParams]);

    const handleHeadersChange = (newValue) => {
        setCustomHeaders(newValue);
        if (screen === 'GET_DATA' || screen === 'SEND_DATA') {
            const encryptedVal = encryption({ HTTP_CUSTOM_HEADER_FIELDS: newValue });
            onInputChange(
                'HTTP_CUSTOM_HEADER_FIELDS',
                (!newValue || newValue.trim() === '') ? null : encryptedVal?.HTTP_CUSTOM_HEADER_FIELDS
            );
        } else {
            onInputChange('HTTP_CUSTOM_HEADER_FIELDS', newValue);
        }
    };

    const handleQueryParamsChange = (newValue) => {
        setQueryParams(newValue);
        onInputChange('HTTP_QUERY_PARAMETERS', newValue);
    };

    const workflowMappings = {
        [t("LBL_URL")]: {
            stateSetter: setUrl,
            key: "CONNECTIONSTRING",
            value: url,
        },
        [t("LBL_KEY_ALIAS")]: {
            stateSetter: setPrivateKey,
            key: "KEY_ALIAS",
            value: privateKey,
        },
        [t("LBL_KEY_ALIAS_TO_USE")]: {
            stateSetter: setKeyAlias,
            key: "KEY_ALIAS",
            value: keyAlias,
        },
        [t("LBL_USERNAME")]: {
            stateSetter: setUsername,
            key: "USERNAME",
            value: username,
        },
        [t("LBL_WS_PASSWORD")]: {
            stateSetter: setPassword,
            key: "PASSWORD",
            value: password,
        },
        [t("TOKEN_NAME")]: {
            stateSetter: setTokenName,
            key: "TOKEN_NAME",
            value: tokenName,
        },
        [t("OAUTH2_TOKEN")]: { stateSetter: setOauth2Token, key: "OAUTH2_TOKEN", value: oauth2Token },
        [t("OAUTH2_HEADER_PREFIX")]: { stateSetter: setOauth2HeaderPrefix, key: "OAUTH2_HEADER_PREFIX", value: oauth2HeaderPrefix },
        [t("OAUTH2_ACCESS_TOKEN_URL")]: { stateSetter: setOauth2AccessTokenUrl, key: "OAUTH2_ACCESS_TOKEN_URL", value: oauth2AccessTokenUrl },
        [t("LBL_CLIENT_ID")]: { stateSetter: setOauth2ClientId, key: "OAUTH2_CLIENT_ID", value: oauth2ClientId },
        [t("LBL_CLIENT_SECRET")]: { stateSetter: setOauth2ClientSecret, key: "OAUTH2_CLIENT_SECRET", value: oauth2ClientSecret },
        [t("OAUTH2_USERNAME_LBL")]: { stateSetter: setOauth2Username, key: "OAUTH2_USERNAME", value: oauth2Username },
        [t("OAUTH2_PASSWORD_LBL")]: { stateSetter: setOauth2Password, key: "OAUTH2_PASSWORD", value: oauth2Password },
        [t("OAUTH2_SCOPE")]: { stateSetter: setOauth2Scope, key: "OAUTH2_SCOPE", value: oauth2Scope },
        [t("OAUTH2_STATE")]: { stateSetter: setOauth2State, key: "OAUTH2_STATE", value: oauth2State },
        [t("OAUTH2_AUTHORIZATION_URL")]: { stateSetter: setOauth2AuthorizationUrl, key: "OAUTH2_AUTHORIZATION_URL", value: oauth2AuthorizationUrl },
        [t("OAUTH2_REDIRECT_URI")]: { stateSetter: setOauth2RedirectUri, key: "OAUTH2_REDIRECT_URI", value: oauth2RedirectUri },
        [t("OAUTH2_AUTHORIZATION_CODE")]: { stateSetter: setOauth2AuthorizationCode, key: "OAUTH2_AUTHORIZATION_CODE", value: oauth2AuthorizationCode },
        [t("OAUTH2_CODE_VERIFIER")]: { stateSetter: setOauth2CodeVerifier, key: "OAUTH2_CODE_VERIFIER", value: oauth2CodeVerifier },
        [t("OAUTH2_AUTHORIZATION_ADDITIONAL_PARAMS")]: { stateSetter: setOauth2AuthorizationAdditionalParams, key: "OAUTH2_AUTHORIZATION_ADDITIONAL_PARAMS", value: oauth2AuthorizationAdditionalParams },
        [t("OAUTH2_REFRESH_TOKEN_URL")]: { stateSetter: setOauth2RefreshTokenUrl, key: "OAUTH2_REFRESH_TOKEN_URL", value: oauth2RefreshTokenUrl },
    };

    const valuesHandler = (value) => {
        if (workflowMappings[valueLabel]) {
            const { stateSetter, key } = workflowMappings[valueLabel];
            if (key === 'PASSWORD' || key === 'OAUTH2_PASSWORD') {
                const encryptedValue = key === "PASSWORD" && !shouldEncryptDecryptPassword
                    ? value
                    : encryptString(value, process.env.REACT_APP_SECRET_KEY2);
                stateSetter(value);
                onInputChange(key, encryptedValue);
            } else {
                stateSetter(value);
                onInputChange(key, value);
            }
        }
    };

    const getFontWeight = (control, currentValue, useConnection) => {
        if (!repoPDC) return "normal";
        return repoPDC?.[control] !== currentValue &&
            useConnection === "preDefined"
            ? "bold"
            : "normal";
    };

    const fontWeights = useMemo(() => {
        return {
            url: getFontWeight("CONNECTIONSTRING", url, useConnection),
            privateKey: getFontWeight("KEY_ALIAS", privateKey, useConnection),
            keyAlias: getFontWeight("KEY_ALIAS", keyAlias, useConnection),
            username: getFontWeight("USERNAME", username, useConnection),
            password: getFontWeight("PASSWORD", password, useConnection),
            requestType: getFontWeight("REQUEST_TYPE", requestType, useConnection),
            contentType: getFontWeight("CONTENT_TYPE", contentType, useConnection),
            soapAction: getFontWeight("SOAP_ACTION", soapAction, useConnection),
            authType: getFontWeight("AUTHENTICATE", authType === 'NO_AUTH' ? 'FALSE' : authType, useConnection),
            tokenName: getFontWeight("TOKEN_NAME", tokenName, useConnection),
        };
    }, [url, privateKey, keyAlias, username, password, requestType, contentType, soapAction, authType, tokenName, useConnection, repoPDC]);

    const requestTypesDropDownItems = requestTypeOptions.map((obj, key) => (
        <MenuItem key={key} value={obj.value}>
            {obj.text}
        </MenuItem>
    ));

    const debouncedContentUpdate = debounce(handleBodyContent, 500);

    const onInputChange = (name, value, pair = {}) => {
        if(['GET_DATA', 'SEND_DATA'].includes(screen)){
            if(name === 'REQUEST_TYPE' && value !== 'POST'){
               setContent({add: {[name]: value, SOURCE_TYPE: 'TRANSLATED_DATA', FORM_DATA: null}})
            }
            else if(!name && Object.keys(pair).length > 0){
                setContent({add: {...pair}})
            }
            else {
                setContent((prev) => ({
                    ...(prev && !Object.prototype.hasOwnProperty.call(prev, 'add')
                        ? prev
                        : { ...(connectionDetails || {}) }),
                    [name]: value,
                }));
            }
            return;
        }
        if (!name && Object.keys(pair).length > 0) {
            setContent((prev) => ({ ...prev, ...pair }));
        } else {
            setContent((prev) => ({ ...prev, [name]: value }));
        }
    }

    const setHeaders = (val) => {
        setCustomHeaders(val);
        // onInputChange('HTTP_CUSTOM_HEADER_FIELDS', val);
    }

    const updateDetails = () => {
        dispatch(api.endpoints.getPdcDetail.initiate({
            account,
            direction: direction,
            name: encodeURIComponent(connectionName)
        }, { forceRefetch: true })).then(res => {
            if (res?.data?.current_state === 'SUCCESS' && res?.data?.data) setCustomHeaders(res.data?.data?.props?.HTTP_CUSTOM_HEADER_FIELDS);
            if (res?.data?.current_state === 'ERROR') setShowAlert({ mssg: res.data.message, isOpen: true, type: 'warning' });
        })
        setOpenModal(false);
    }

    const handleCancel = () => {
        if(screen === "GET_DATA" || screen === "SEND_DATA"){
            setOpenModal(false);
            return;
        }

        updateDetails();
    }

    const handleAlertClose = () => {
        setShowAlert({ ...showAlert, isOpen: false });
    }

    const handleDone = (newData) => {
        if(screen === "GET_DATA" || screen === "SEND_DATA"){
            const encryptedVal = encryption({HTTP_CUSTOM_HEADER_FIELDS: newData});
            onInputChange('HTTP_CUSTOM_HEADER_FIELDS', newData.trim() === '' ? null : encryptedVal?.HTTP_CUSTOM_HEADER_FIELDS);
            setOpenModal(false);
            return;
        }
        onInputChange('HTTP_CUSTOM_HEADER_FIELDS', newData);
        
        const body = {
            ...connectionDetails,
            connectionName,
            HTTP_CUSTOM_HEADER_FIELDS: newData,
            ADAPTER_TYPE: 'HTTP'
        }
        dispatch(treeActions.setConnection({
            screen: 'inbound',
            value: { connectionName, ...body }
        }));
        dispatch(api.endpoints.createPdc.initiate({
            account,
            direction: direction,
            name: encodeURIComponent(connectionName),
            body: encryption(body),
            oldPdcName: encodeURIComponent(connectionName),
        })).then(res => {
            if (res?.data?.current_state === 'SUCCESS') updateDetails();
            else setShowAlert({ mssg: res?.data?.message, isOpen: true, type: 'error' });
        });
        setOpenModal(false)
    }

    useEffect(() => {
        dispatch(api.endpoints.getPrivateKeys.initiate({private_key: true}, { forceRefetch: true })).then((res) => {
            if (res?.data?.current_state === 'SUCCESS') {
                setPrivateKeys(res?.data?.data);
            } else setPrivateKeys([]);
        })
        dispatch(api.endpoints.getPrivateKeys.initiate({private_key: false}, { forceRefetch: true })).then((res) => {
            if (res?.data?.current_state === 'SUCCESS') {
                setKeyAliases(res?.data?.data);
            } else setKeyAliases([]);
        })
    }, [direction]);

    useEffect(() => {
        if(!_.isEqual(content, connectionDetails)) debouncedContentUpdate(content);
    }, [content]);

    useEffect(() => {
        setUrl(connectionDetails?.CONNECTIONSTRING);
        setPrivateKey(connectionDetails?.KEY_ALIAS || '');
        setUsername(connectionDetails?.USERNAME || '');
        setPassword(
            connectionDetails?.PASSWORD
                ? shouldEncryptDecryptPassword
                    ? decryptString(connectionDetails?.PASSWORD, process.env.REACT_APP_SECRET_KEY2)
                    : connectionDetails?.PASSWORD
                : ''
        );
        setCustomHeaders(connectionDetails?.HTTP_CUSTOM_HEADER_FIELDS || '');
        setRequestType(connectionDetails?.REQUEST_TYPE || 'GET');
        setContentType(connectionDetails?.CONTENT_TYPE || ' ');
        setSoapAction(connectionDetails?.SOAP_ACTION || '');
        setTokenName(connectionDetails?.TOKEN_NAME || '');
        if (direction === 'OUT') {
            setKeyAlias(connectionDetails?.KEY_ALIAS || '');
        }
        setOauth2Token(connectionDetails?.OAUTH2_TOKEN || '');
        setOauth2HeaderPrefix(connectionDetails?.OAUTH2_HEADER_PREFIX || '');
        setOauth2AutoRefresh(connectionDetails?.OAUTH2_AUTO_REFRESH_TOKEN === 'TRUE' || connectionDetails?.OAUTH2_AUTO_REFRESH_TOKEN === true);
        setOauth2AddTo(connectionDetails?.OAUTH2_ADD_TO || 'REQUEST_HEADER');
        // setOauth2GrantType(connectionDetails?.OAUTH2_GRANT_TYPE || 'PASSWORD_CREDENTIALS');
        const loadedGrantType = connectionDetails?.OAUTH2_GRANT_TYPE;
        setOauth2GrantType(
            !loadedGrantType || loadedGrantType === 'IMPLICIT' ? 'PASSWORD_CREDENTIALS' : loadedGrantType
        );
        setOauth2AccessTokenUrl(connectionDetails?.OAUTH2_ACCESS_TOKEN_URL || '');
        setOauth2ClientId(connectionDetails?.OAUTH2_CLIENT_ID || '');
        setOauth2ClientSecret(connectionDetails?.OAUTH2_CLIENT_SECRET || '');
        setOauth2Username(connectionDetails?.OAUTH2_USERNAME || '');
        setOauth2Password(connectionDetails?.OAUTH2_PASSWORD ? decryptString(connectionDetails.OAUTH2_PASSWORD, process.env.REACT_APP_SECRET_KEY2) : '');
        setOauth2ClientAuth(connectionDetails?.OAUTH2_CLIENT_AUTHENTICATION || '');
        setOauth2Scope(connectionDetails?.OAUTH2_SCOPE || '');
        setOauth2State(connectionDetails?.OAUTH2_STATE || '');
        setOauth2RefreshTokenUrl(connectionDetails?.OAUTH2_REFRESH_TOKEN_URL || '');
        setJwtAddTo(connectionDetails?.ADD_AUTHORIZATION_TO || 'REQUEST_HEADER');
        setOauth2AuthorizationUrl(connectionDetails?.OAUTH2_AUTHORIZATION_URL || '');
        setOauth2RedirectUri(connectionDetails?.OAUTH2_REDIRECT_URI || '');
        setOauth2AuthorizationCode(connectionDetails?.OAUTH2_AUTHORIZATION_CODE || '');
        setOauth2CodeVerifier(connectionDetails?.OAUTH2_CODE_VERIFIER || '');
        setOauth2AuthorizationAdditionalParams(connectionDetails?.OAUTH2_AUTHORIZATION_ADDITIONAL_PARAMS || '');
        setQueryParams(connectionDetails?.HTTP_QUERY_PARAMETERS || '');
        const authenticateValue = connectionDetails?.AUTHENTICATE;
        const hasUsername = Boolean(connectionDetails?.USERNAME);
        const hasPassword = Boolean(connectionDetails?.PASSWORD);
        if (!authenticateValue) {
            setAuthType(hasUsername || hasPassword ? 'TRUE' : 'NO_AUTH');
        } else {
            // Normalize possible stored values (constants or localized text)
            const localizedNoAuth = t('NO_AUTH');
            const localizedBasic = t('BASIC_AUTH');
            const localizedBghttpBasic = t('BGHTTP_BASIC_AUTH');
            const localizedBearer = t('BEARER_TOKEN');
            const localizedJwt = t('JWT_BEARER');
            const localizedOAuth2 = t('OAUTH2');

            if (
                authenticateValue === 'NO_AUTH' ||
                authenticateValue === localizedNoAuth
            ) {
                setAuthType('NO_AUTH');
            } else if (
                authenticateValue === 'TRUE' ||
                authenticateValue === localizedBasic ||
                authenticateValue === localizedBghttpBasic
            ) {
                setAuthType('TRUE');
            } else if (
                authenticateValue === 'BEARER_TOKEN' ||
                authenticateValue === localizedBearer
            ) {
                setAuthType('BEARER_TOKEN');
            } else if (
                authenticateValue === 'JWT_BEARER' ||
                authenticateValue === localizedJwt
            ) {
                setAuthType('JWT_BEARER');
            } else if (
                authenticateValue === 'OAUTH2' ||
                authenticateValue === localizedOAuth2
            ) {
                setAuthType('OAUTH2');
            } else {
                // Fallback – use raw value so we at least show something
                setAuthType(authenticateValue);
            }
        }
        setContent({ ...connectionDetails });
    }, [connectionDetails]);

    return (
        <>
            {direction === 'IN' && (
                <div style={{ display: "flex", gap: "4px" }}>
                    <Autocomplete
                        disablePortal
                        className='width-100'
                        options={privateKeys}
                        value={privateKey}
                        disabled={disableControl || false}
                        sx={{ maxHeight: 300 }}
                        onChange={(event, value) => {
                            onInputChange('KEY_ALIAS', value);
                            setPrivateKey(value);
                        }}
                        renderInput={(params) => <TextField
                            {...params}
                        label={t("LBL_KEY_ALIAS")}
                            value={privateKey}
                            inputProps={{
                                ...params.inputProps,
                                style: { fontWeight: fontWeights.privateKey }
                            }}
                            onChange={(e) => {
                                onInputChange('KEY_ALIAS', e.target.value);
                                setPrivateKey(e.target.value);
                            }}
                            variant="outlined"
                            size="small"
                            name={'KEY_ALIAS'}
                            autoComplete="off"
                        />
                        }
                    />
                    {["GET_DATA", "SEND_DATA"].includes(screen) && (
                        <WorkflowSessionModalButton
                            disableControl={disableControl || false}
                            label={t("LBL_KEY_ALIAS")}
                            setWorkflowSessionModal={setWorkflowSessionModal}
                            setValueLabel={setValueLabel}
                        />
                    )}
                </div>
            )}
            
            {
                direction === 'OUT' && <>
                    
                    <FormControl className="width-100" size="small">
                        <TextField
                            variant="outlined"
                            size="small"
                            label={t("LBL_SOAP_ACTION")}
                            type="text"
                            value={soapAction || ''}
                            disabled={disableControl || false}
                            inputProps={{
                                style: { fontWeight: fontWeights.soapAction }
                            }}
                            onChange={(e) => {
                                onInputChange(e.target.name, e.target.value);
                                setSoapAction(e.target.value);
                            }}
                            className={"width-100"}
                            name={"SOAP_ACTION"}
                            autoComplete="off"
                        />
                    </FormControl>
                    <div style={{ display: "flex", gap: "4px" }}>
                        <Autocomplete
                            disablePortal
                            className='width-100'
                            options={keyAliases}
                            value={keyAlias}
                            disabled={disableControl || false}
                            sx={{ maxHeight: 300 }}
                            onChange={(e, value) => {
                                onInputChange('KEY_ALIAS', value);
                                setKeyAlias(value);
                            }}
                            renderInput={(params) => <TextField
                                {...params}
                                label={t("LBL_KEY_ALIAS_TO_USE")}
                                value={keyAlias}
                                inputProps={{
                                    ...params.inputProps,
                                    style: { fontWeight: fontWeights.keyAlias }
                                }}
                                onChange={(event, value) => {
                                    onInputChange('KEY_ALIAS', event.target.value);
                                    setKeyAlias(event.target.value);
                                }}
                                variant="outlined"
                                size="small"
                                name={'KEY_ALIAS'}
                                autoComplete="off"
                            />
                            }
                        />
                        {["GET_DATA", "SEND_DATA"].includes(screen) && (
                            <WorkflowSessionModalButton
                                disableControl={disableControl || false}
                                label={t("LBL_KEY_ALIAS_TO_USE")}
                                setWorkflowSessionModal={setWorkflowSessionModal}
                                setValueLabel={setValueLabel}
                            />
                        )}
                    </div>
                </>
            }

            <div style={{ display: "flex", gap: "8px", alignItems: "flex-end" }}>
                <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                    <span style={{ fontSize: "12px", color: "rgba(0,0,0,0.6)", paddingLeft: "2px" }}>{t("LBL_REQUEST_TYPE")}</span>
                    <FormControl variant="outlined" size="small" sx={{ minWidth: "120px" }}>
                        <Select
                            disabled={disableControl || false}
                            name={"REQUEST_TYPE"}
                            id="requestTypeDropdown"
                            value={direction === 'IN' ? 'GET' : requestType}
                            sx={{
                                '& .MuiSelect-select': {
                                    fontWeight: fontWeights.requestType,
                                    padding: '7.1px 14px',
                                },
                             }}
                            onChange={(e) => {
                                onInputChange(e.target.name, e.target.value);
                                setRequestType(e.target.value);
                            }}
                            MenuProps={{
                                PaperProps: { sx: { maxHeight: 300 } },
                            }}
                        >
                            {(direction === 'IN'
                                ? ['GET']
                                : ['DELETE', 'GET', 'OPTIONS', 'PATCH', 'POST', 'PUT']
                            ).map((type) => (
                                <MenuItem key={type} value={type}>{type}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </div>
                <div style={{ display: "flex", gap: "4px", flex: 1, flexDirection: "column" }}>
                    <span style={{ fontSize: "12px", color: "rgba(0,0,0,0.6)", paddingLeft: "2px" }}>{t("LBL_URL")}</span>
                    <div style={{ display: "flex", gap: "4px" }}>
                        <FormControl className="width-100" size="small">
                            <TextField
                                variant="outlined"
                                size="small"
                                type="text"
                                value={url}
                                disabled={disableControl || false}
                                inputProps={{
                                    style: { fontWeight: fontWeights.url }
                                }}
                                onChange={(e) => {
                                    onInputChange(e.target.name, e.target.value);
                                    setUrl(e.target.value);
                                }}
                                className={"width-100"}
                                name={"CONNECTIONSTRING"}
                                autoComplete="off"
                            />
                        </FormControl>
                        {["GET_DATA", "SEND_DATA"].includes(screen) && (
                            <WorkflowSessionModalButton
                                disableControl={disableControl || false}
                                label={t("LBL_URL")}
                                setWorkflowSessionModal={setWorkflowSessionModal}
                                setValueLabel={setValueLabel}
                            />
                        )}
                    </div>
                </div>
            </div>

            {
                direction === 'OUT' && <>
                <Autocomplete
                        disablePortal
                        options={contentTypeOption}
                        value={contentType}
                        disabled={disableControl || false}
                        sx={{ maxHeight: 300 }}
                        onChange={(e, value) => {
                            onInputChange('CONTENT_TYPE', value);
                            setContentType(value);
                        }}
                        renderInput={(params) => <TextField
                            {...params}
                            label={t("LBL_CONTENT_TYPE")}
                            value={contentType}
                            inputProps={{
                                ...params.inputProps,
                                style: { fontWeight: fontWeights.contentType }
                            }}
                            onChange={(event, value) => {
                                onInputChange('CONTENT_TYPE', event.target.value);
                                setContentType(event.target.value);
                            }}
                            variant="outlined"
                            size="small"
                            name={'TRADING_PARTNER'}
                            autoComplete="off"
                        />
                        }
                    />
                </>
            }

            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, mt: 1 }}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs
                        value={activeTab}
                        onChange={(_, newValue) => setActiveTab(newValue)}
                        variant="scrollable"
                        scrollButtons="auto"
                    >
                        <Tab label={t("LBL_TAB_SETTINGS")} />
                        <Tab label={
                            <TabBadge badgeContent={authCount} color="primary">
                                <span style={{ paddingRight: authCount ? '10px' : 0 }}>{t("LBL_TAB_AUTHORIZATION")}</span>
                            </TabBadge>
                        } />
                        <Tab label={
                            <TabBadge badgeContent={headersCount} color="primary">
                                <span style={{ paddingRight: headersCount ? '10px' : 0 }}>{t("LBL_TAB_HEADERS")}</span>
                            </TabBadge>
                        } />
                        <Tab label={
                            <TabBadge badgeContent={queryParamsCount} color="primary">
                                <span style={{ paddingRight: queryParamsCount ? '10px' : 0 }}>{t("LBL_TAB_QUERY_PARAMETERS")}</span>
                            </TabBadge>
                        } />
                    </Tabs>
                </Box>

                <Box sx={{ pt: 1, px: 2, pb: 2, minHeight: 260 }}>
                <TabPanel value={activeTab} index={0}>
                        <Settings
                            connectionDetails={connectionDetails}
                            direction={direction}
                            disableControl={disableControl}
                            screen={screen}
                            onInputChange={onInputChange}
                            account={account}
                            repoPDC={repoPDC}
                            useConnection={useConnection}
                        />
                </TabPanel>
                <TabPanel value={activeTab} index={1}>
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'minmax(220px, 260px) 1fr',
                            columnGap: '24px',
                            alignItems: 'flex-start',
                            paddingTop: '8px',
                        }}
                    >
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <FormControl className="width-100" size="small">
                                <TextField
                                    select
                                    variant="outlined"
                                    size="small"
                                    label={t("LBL_AUTH_TYPE")}
                                    name="AUTHENTICATE"
                                    value={authType}
                                    disabled={disableControl || false}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        setAuthType(value);

                                        // When switching auth types, clear the fields that belong to other auth modes.
                                        // Parent screens support `value.add` payloads and will delete keys when the value is null.
                                        const storedValue = value === 'NO_AUTH' ? 'FALSE' : value;

                                        const oauth2KeysToClear = [
                                            'OAUTH2_TOKEN',
                                            'OAUTH2_HEADER_PREFIX',
                                            'OAUTH2_AUTO_REFRESH_TOKEN',
                                            'OAUTH2_ADD_TO',
                                            'OAUTH2_GRANT_TYPE',
                                            'OAUTH2_ACCESS_TOKEN_URL',
                                            'OAUTH2_CLIENT_ID',
                                            'OAUTH2_CLIENT_SECRET',
                                            'OAUTH2_USERNAME',
                                            'OAUTH2_PASSWORD',
                                            'OAUTH2_CLIENT_AUTHENTICATION',
                                            'OAUTH2_SCOPE',
                                            'OAUTH2_STATE',
                                            'OAUTH2_REFRESH_TOKEN_URL',
                                            'OAUTH2_AUTHORIZATION_URL',
                                            'OAUTH2_REDIRECT_URI',
                                            'OAUTH2_AUTHORIZATION_CODE',
                                            'OAUTH2_CODE_VERIFIER',
                                            'OAUTH2_AUTHORIZATION_ADDITIONAL_PARAMS',
                                        ];

                                        const jwtKeysToClear = [
                                            'ADD_AUTHORIZATION_TO',
                                            'JWT_ALGORITHM',
                                            'JWT_SECRET',
                                            'JWT_SECRET_BASE64_ENCODED',
                                            'JWT_PAYLOAD',
                                            'JWT_REQUEST_HEADER_PREFIX',
                                            'JWT_HEADERS',
                                        ];

                                        const shouldClearBasic = value === 'NO_AUTH' || value === 'BEARER_TOKEN' || value === 'JWT_BEARER' || value === 'OAUTH2';
                                        const shouldClearBearer = value !== 'BEARER_TOKEN';
                                        const shouldClearOauth2 = value !== 'OAUTH2';
                                        const shouldClearJwt = value !== 'JWT_BEARER';

                                        const clearPayload = {};

                                        if (shouldClearBasic) {
                                            clearPayload.USERNAME = null;
                                            clearPayload.PASSWORD = null;
                                        }
                                        if (shouldClearBearer) {
                                            clearPayload.TOKEN_NAME = null;
                                        }
                                        if (shouldClearOauth2) {
                                            oauth2KeysToClear.forEach((k) => { clearPayload[k] = null; });
                                        }
                                        if (shouldClearJwt) {
                                            jwtKeysToClear.forEach((k) => { clearPayload[k] = null; });
                                        }

                                        const enableSync = value !== 'NO_AUTH';
                                        const syncPayload = enableSync ? { SYNCHRONOUS: stringifyJSONAttributes(true) } : {};
                                        // Cancel any in-flight debounced body updates so older auth field values don't
                                        // get written back right after we clear them.
                                        debouncedContentUpdate.cancel?.();

                                        // !screen means we are in the connection screen (PDC)
                                        if (!screen) {
                                            if (enableSync) {
                                                Object.entries(syncPayload).forEach(([sk, sv]) => {
                                                    if (sv !== undefined) onInputChange(sk, sv);
                                                });
                                            }
                                            onInputChange(e.target.name, storedValue);
                                        } else {
                                            handleBodyContent({
                                                add: {
                                                    [e.target.name]: storedValue,
                                                    ...clearPayload,
                                                    ...syncPayload,
                                                }
                                            });
                                        }

                                        // Also clear local UI state so the newly selected auth pane shows correct values immediately.
                                        if (!screen && shouldClearBasic) {
                                            setUsername('');
                                            setPassword('');
                                        }
                                        if (!screen && shouldClearBearer) {
                                            setTokenName('');
                                        }
                                        if (!screen && shouldClearOauth2) {
                                            setOauth2Token('');
                                            setOauth2HeaderPrefix('');
                                            setOauth2AutoRefresh(false);
                                            setOauth2AddTo('REQUEST_HEADER');
                                            setOauth2GrantType('PASSWORD_CREDENTIALS');
                                            setOauth2AccessTokenUrl('');
                                            setOauth2ClientId('');
                                            setOauth2ClientSecret('');
                                            setOauth2Username('');
                                            setOauth2Password('');
                                            setOauth2ClientAuth('');
                                            setOauth2Scope('');
                                            setOauth2State('');
                                            setOauth2RefreshTokenUrl('');
                                            setOauth2AuthorizationUrl('');
                                            setOauth2RedirectUri('');
                                            setOauth2AuthorizationCode('');
                                            setOauth2CodeVerifier('');
                                        }
                                        if (shouldClearJwt) {
                                            setJwtAddTo('REQUEST_HEADER');
                                        }
                                    }}
                                    inputProps={{
                                        style: { fontWeight: fontWeights.authType }
                                    }}
                                    className={"width-100"}
                                >
                                    <MenuItem value="NO_AUTH">{t('NO_AUTH')}</MenuItem>
                                    <MenuItem value="TRUE">{t('BASIC_AUTH')}</MenuItem>
                                    <MenuItem value="BEARER_TOKEN">{t('BEARER_TOKEN')}</MenuItem>
                                    <MenuItem value="JWT_BEARER">{t('JWT_BEARER')}</MenuItem>
                                    <MenuItem value="OAUTH2">{t('OAUTH2')}</MenuItem>
                                </TextField>
                            </FormControl>
                            {authType !== 'NO_AUTH' && (
                                <Typography variant="body2" color="textSecondary">
                                    {authType === 'TRUE' && (
                                        <>
                                            {t('HTTP_AUTH_HEADER_ADDED')}{' '}
                                            <span
                                                style={{ fontWeight: 600, textDecoration: 'underline', cursor: 'pointer' }}
                                                onClick={() =>
                                                    dispatch(
                                                        treeActions.setShowHelpPanel({
                                                            docType: 'HTTP_AUTH_BASIC',
                                                            isOpen: true,
                                                        })
                                                    )
                                                }
                                            >
                                                {t('LBL_HTTP_HELP_BASIC_AUTH')}
                                            </span>
                                        </>
                                    )}
                                    {authType === 'BEARER_TOKEN' && (
                                        <>
                                            {t('HTTP_AUTH_HEADER_ADDED')}{' '}
                                            <span
                                                style={{ fontWeight: 600, textDecoration: 'underline', cursor: 'pointer' }}
                                                onClick={() =>
                                                    dispatch(
                                                        treeActions.setShowHelpPanel({
                                                            docType: 'HTTP_AUTH_BEARER',
                                                            isOpen: true,
                                                        })
                                                    )
                                                }
                                            >
                                                {t('LBL_HTTP_HELP_BEARER_TOKEN')}
                                            </span>
                                        </>
                                    )}
                                    {authType === 'JWT_BEARER' && (
                                        <>
                                            {t('HTTP_AUTH_HEADER_GENERATED')}{' '}
                                            <span
                                                style={{ fontWeight: 600, textDecoration: 'underline', cursor: 'pointer' }}
                                                onClick={() =>
                                                    dispatch(
                                                        treeActions.setShowHelpPanel({
                                                            docType: 'HTTP_AUTH_JWT_BEARER',
                                                            isOpen: true,
                                                        })
                                                    )
                                                }
                                            >
                                                {t('LBL_HTTP_HELP_JWT_BEARER')}
                                            </span>
                                        </>
                                    )}
                                    {authType === 'OAUTH2' && (
                                        <>
                                            {t('HTTP_AUTH_HEADER_GENERATED')}{' '}
                                            <span
                                                style={{ fontWeight: 600, textDecoration: 'underline', cursor: 'pointer' }}
                                                onClick={() =>
                                                    dispatch(
                                                        treeActions.setShowHelpPanel({
                                                            docType: 'HTTP_AUTH_OAUTH2',
                                                            isOpen: true,
                                                        })
                                                    )
                                                }
                                            >
                                                {t('LBL_HTTP_HELP_OAUTH2')}
                                            </span>
                                        </>
                                    )}
                                </Typography>
                            )}
                            {authType === 'OAUTH2' && (
                                <FormControl size="small" sx={{ mt: 1.5, maxWidth: 260 }}>
                                    <TextField
                                        select
                                        variant="outlined"
                                        size="small"
                                        label={t('OAUTH2_ADD_TO')}
                                        value={oauth2AddTo || 'REQUEST_HEADER'}
                                        disabled={disableControl || false}
                                        onChange={(e) => {
                                            setOauth2AddTo(e.target.value);
                                            onInputChange('OAUTH2_ADD_TO', e.target.value);
                                        }}
                                        className="width-100"
                                    >
                                        {AddToOptions.map((opt) => (
                                            <MenuItem key={opt.value} value={opt.value}>
                                                {t(opt.labelKey)}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </FormControl>
                            )}
                            {authType === 'JWT_BEARER' && (
                                <FormControl size="small" sx={{ mt: 1.5, maxWidth: 260 }}>
                                    <TextField
                                        select
                                        variant="outlined"
                                        size="small"
                                        label={t('JWT_ADD_TO_LABEL')}
                                        value={jwtAddTo || 'REQUEST_HEADER'}
                                        disabled={disableControl || false}
                                        onChange={(e) => {
                                            setJwtAddTo(e.target.value);
                                            onInputChange('ADD_AUTHORIZATION_TO', e.target.value);
                                        }}
                                        className="width-100"
                                    >
                                        {JWT_ADD_TO_OPTIONS.map((opt) => (
                                            <MenuItem key={opt.value} value={opt.value}>
                                                {t(opt.labelKey)}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </FormControl>
                            )}
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            {authType === 'NO_AUTH' && (
                                <NoAuthFields t={t} />
                            )}
                            {authType === 'TRUE' && (
                                <BasicAuthFields
                                    t={t}
                                    username={username}
                                    password={password}
                                    shouldEncryptDecryptPassword={shouldEncryptDecryptPassword}
                                    disableControl={disableControl}
                                    screen={screen}
                                    fontWeights={fontWeights}
                                    onInputChange={onInputChange}
                                    setUsername={setUsername}
                                    setPassword={setPassword}
                                    setWorkflowSessionModal={setWorkflowSessionModal}
                                    setValueLabel={setValueLabel}
                                />
                            )}
                            {authType === 'BEARER_TOKEN' && (
                                <BearerTokenFields
                                    t={t}
                                    tokenName={tokenName}
                                    disableControl={disableControl}
                                    fontWeights={fontWeights}
                                    onInputChange={onInputChange}
                                    setTokenName={setTokenName}
                                    screen={screen}
                                    setWorkflowSessionModal={setWorkflowSessionModal}
                                    setValueLabel={setValueLabel}
                                />
                            )}
                            {authType === 'JWT_BEARER' && (
                                <JwtBearerFields
                                    connectionDetails={connectionDetails}
                                    repoPDC={repoPDC}
                                    useConnection={useConnection}
                                    disableControl={disableControl}
                                    screen={screen}
                                    onInputChange={onInputChange}
                                />
                            )}
                            {authType === 'OAUTH2' && (
                                <OAuth2Fields
                                    t={t}
                                    disableControl={disableControl}
                                    screen={screen}
                                    onInputChange={onInputChange}
                                    setWorkflowSessionModal={setWorkflowSessionModal}
                                    setValueLabel={setValueLabel}
                                    fontWeights={fontWeights}
                                    oauth2Token={oauth2Token}
                                    setOauth2Token={setOauth2Token}
                                    oauth2HeaderPrefix={oauth2HeaderPrefix}
                                    setOauth2HeaderPrefix={setOauth2HeaderPrefix}
                                    oauth2AutoRefresh={oauth2AutoRefresh}
                                    setOauth2AutoRefresh={(v) => { setOauth2AutoRefresh(v); onInputChange('OAUTH2_AUTO_REFRESH_TOKEN', stringifyJSONAttributes(v)); }}
                                    oauth2AddTo={oauth2AddTo}
                                    setOauth2AddTo={setOauth2AddTo}
                                    oauth2GrantType={oauth2GrantType}
                                    setOauth2GrantType={setOauth2GrantType}
                                    oauth2AuthorizationUrl={oauth2AuthorizationUrl}
                                    setOauth2AuthorizationUrl={setOauth2AuthorizationUrl}
                                    oauth2RedirectUri={oauth2RedirectUri}
                                    setOauth2RedirectUri={setOauth2RedirectUri}
                                    oauth2AuthorizationCode={oauth2AuthorizationCode}
                                    setOauth2AuthorizationCode={setOauth2AuthorizationCode}
                                    oauth2CodeVerifier={oauth2CodeVerifier}
                                    setOauth2CodeVerifier={setOauth2CodeVerifier}
                                    oauth2AuthorizationAdditionalParams={oauth2AuthorizationAdditionalParams}
                                    setOauth2AuthorizationAdditionalParams={setOauth2AuthorizationAdditionalParams}
                                    oauth2AccessTokenUrl={oauth2AccessTokenUrl}
                                    setOauth2AccessTokenUrl={setOauth2AccessTokenUrl}
                                    oauth2ClientId={oauth2ClientId}
                                    setOauth2ClientId={setOauth2ClientId}
                                    oauth2ClientSecret={oauth2ClientSecret}
                                    setOauth2ClientSecret={setOauth2ClientSecret}
                                    oauth2Username={oauth2Username}
                                    setOauth2Username={setOauth2Username}
                                    oauth2Password={oauth2Password}
                                    setOauth2Password={setOauth2Password}
                                    onOauth2PasswordChange={(v) => { setOauth2Password(v); onInputChange('OAUTH2_PASSWORD', encryptString(v, process.env.REACT_APP_SECRET_KEY2)); }}
                                    oauth2ClientAuth={oauth2ClientAuth}
                                    setOauth2ClientAuth={setOauth2ClientAuth}
                                    oauth2Scope={oauth2Scope}
                                    setOauth2Scope={setOauth2Scope}
                                    oauth2State={oauth2State}
                                    setOauth2State={setOauth2State}
                                    oauth2RefreshTokenUrl={oauth2RefreshTokenUrl}
                                    setOauth2RefreshTokenUrl={setOauth2RefreshTokenUrl}
                                    onOauthDevNotify={(msg) => setShowAlert({ mssg: msg, type: 'info', isOpen: true })}
                                />
                            )}
                        </div>
                    </div>
                </TabPanel>
                <TabPanel value={activeTab} index={2}>
                    <React.Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress size={20} /></Box>}>
                        <Headers
                            showAuthHeader={authType !== 'NO_AUTH'}
                            authHeaderValue={authHeaderValue}
                            customHeaders={customHeaders}
                            onHeadersChange={handleHeadersChange}
                            onGoToAuthorization={() => setActiveTab(1)}
                            disableControl={disableControl}
                            showWorkflowSessionButton={['GET_DATA', 'SEND_DATA'].includes(screen)}
                        />
                    </React.Suspense>
                </TabPanel>
                <TabPanel value={activeTab} index={3}>
                    <React.Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress size={20} /></Box>}>
                        <QueryParameters
                            queryParams={queryParams}
                            onQueryParamsChange={handleQueryParamsChange}
                            disableControl={disableControl}
                            showWorkflowSessionButton={['GET_DATA', 'SEND_DATA'].includes(screen)}
                        />
                    </React.Suspense>
                </TabPanel>
                </Box>
            </Box>

            {/* <div style={{ display: "flex", gap: "4px" }}>
                <FormControl className="width-100" size="small">
                    <TextField
                        variant="outlined"
                        size="small"
                        label={t("LBL_USERNAME")}
                        type="text"
                        value={username || ''}
                        disabled={disableControl || false}
                        inputProps={{
                            style: { fontWeight: fontWeights.username }
                        }}
                        onChange={(e) => {
                            onInputChange('USERNAME', e.target.value);
                            setUsername(e.target.value);
                        }}
                        className={"width-100"}
                        name={"USER_NAME"}
                        autoComplete="off"
                    />
                </FormControl>
                {["GET_DATA", "SEND_DATA"].includes(screen) && (
                    <WorkflowSessionModalButton
                        disableControl={disableControl || false}
                        label={t("LBL_USERNAME")}
                        setWorkflowSessionModal={setWorkflowSessionModal}
                        setValueLabel={setValueLabel}
                    />
                )}
            </div> */}
            {/* <div style={{ display: "flex", gap: "4px" }}>
                <SmartPasswordField
                    variant="outlined"
                    size="small"
                    label={t("LBL_WS_PASSWORD")}
                    value={password || ''}
                    disabled={disableControl || false}
                    inputProps={{
                        style: { fontWeight: fontWeights.password }
                    }}
                    onChange={(e) => {
                        onInputChange(
                            'PASSWORD',
                            shouldEncryptDecryptPassword
                                ? encryptString(e.target.value, process.env.REACT_APP_SECRET_KEY2)
                                : e.target.value
                        );
                        setPassword(e.target.value);
                    }}
                    className={"width-100"}
                    name={"PASS_WORD"}
                    autoComplete="off"
                />
                {["GET_DATA", "SEND_DATA"].includes(screen) && (
                    <WorkflowSessionModalButton
                        disableControl={disableControl || false}
                        label={t("LBL_WS_PASSWORD")}
                        setWorkflowSessionModal={setWorkflowSessionModal}
                        setValueLabel={setValueLabel}
                    />
                )}
            </div> */}

            {/* <Button
                className='width-100'
                size="small"
                disabled={disableControl || false}
                onClick={() => {
                    if(screen === "GET_DATA")
                        setCustomHeaders(selectedNode?.INBOUND?.HTTP_CUSTOM_HEADER_FIELDS || '');
                    else if(screen === "SEND_DATA")
                        setCustomHeaders(selectedNode?.OUTBOUND?.HTTP_CUSTOM_HEADER_FIELDS || '');
                    else if (!connectionDetails?.HTTP_CUSTOM_HEADER_FIELDS && connection?.ADAPTER_TYPE === 'HTTP' && connectionName === connection?.connectionName)
                        setCustomHeaders(connection?.HTTP_CUSTOM_HEADER_FIELDS || '')
                    setOpenModal(true)
                }}
                variant="outlined">
                {t("LBL_HTTP_CUSTOM_HEADER_FIELDS")} {`${connectionDetails?.HTTP_CUSTOM_HEADER_FIELDS && (encodeURIComponent(customHeaders) !== '%1F') ? `(${t('SET')})` : ''}`}
            </Button> */}
            {/* {openModal && <CustomHeaders setOpenModal={setOpenModal} handleCancel={handleCancel} handleDone={handleDone} openModal={openModal} customHeaders={customHeaders} setCustomHeaders={setHeaders} />} */}


            <Snackbar open={showAlert.isOpen} onClose={() => handleAlertClose()}>
                <Alert
                    onClose={() => handleAlertClose()}
                    severity={showAlert.type}
                    variant="filled"
                    sx={{ width: "100%" }}>
                    {showAlert.mssg}
                </Alert>
            </Snackbar>

            <WorkflowSessionModal
                valueLabel={valueLabel}
                showAccountData={true}
                showCallingWorkflow={true}
                showSystemData={true}
                showWorkflowSession={true}
                showDateAndTime={true}
                openModal={workflowSessionModal}
                setModalOpen={setWorkflowSessionModal}
                handleClose={() =>
                    setWorkflowSessionModal({ open: false, isFirstTime: false })
                }
                initialValue={workflowMappings[valueLabel]?.value}
                inputType={[t('LBL_WS_PASSWORD'), t('OAUTH2_PASSWORD_LBL')].includes(valueLabel) ? "password" : "text"}
                specialPasswordField={[t('LBL_WS_PASSWORD'), t('OAUTH2_PASSWORD_LBL')].includes(valueLabel)}
                valuesHandler={(value) => {
                    valuesHandler(value);
                }}
            />
        </>
    )
}

const TabBadge = styled(Badge)(() => ({
    '& .MuiBadge-badge': {
        right: 0,
        top: 2,
        border: '1px solid #d4d4d4',
        padding: '0px 4px',
        height: '16px',
        width: '16px',
        minWidth: '16px',
    },
}));

export default Http